import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  Video,
  Plus,
  Users,
  Calendar,
  LogOut,
  Settings,
  Shield,
  Sparkles,
  Crown,
  Loader2,
  Pencil,
  Copy,
} from "lucide-react";
import { format } from "date-fns";

import { EmailVerificationBanner } from "@/components/EmailVerificationBanner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { z } from "zod";
import { handleRateLimitError } from "@/lib/rateLimitHandler";
import { getErrorMessage } from "@/lib/errorMessages";
import { hashPassword } from "@/lib/security";
import { Footer } from "@/components/Footer";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import TemplatePickerModal from "@/components/TemplatePickerModal";
import { getTemplateById } from "@/config/templates";
import {
  addRecentTemplate,
  clearDefaultTemplate,
  clearRecentTemplates,
  getDefaultTemplateId,
  getRecentTemplateIds,
  setDefaultTemplateId,
} from "@/utils/templatePrefs";
import { initializeEditorFromTemplate } from "@/lib/editorInitialization";
import { generateMeetingCode as generateCode, getMeetingLink } from "@/lib/meetingUtils";
import { getSupabaseAuthHeaders } from "@/lib/supabaseAuth";
import { useActivityFeed } from "@/hooks/useActivityFeed";
import { EditProfileDialog } from "@/components/EditProfileDialog";
import { useTranslation } from "react-i18next";

const meetingSchema = z.object({
  title: z.string().trim().min(1, "Title is required").max(200, "Title must be less than 200 characters"),
  description: z.string().max(1000, "Description must be less than 1000 characters").optional(),
  code: z.string().refine((code) => {
    const clean = code.replace(/-/g, '').toUpperCase();
    return /^[A-Z0-9]{9,12}$/.test(clean);
  }, "Invalid meeting code format"),
  password: z
    .string()
    .min(4, "Password must be at least 4 characters")
    .max(50, "Password must be under 50 characters")
    .optional(),
});

interface Meeting {
  id: string;
  title: string;
  description: string | null;
  meeting_code: string;
  is_active: boolean;
  scheduled_start: string | null;
  scheduled_end: string | null;
  created_at: string;
  has_summary?: boolean;
}

interface Profile {
  display_name: string;
  avatar_url: string | null;
}

export default function Dashboard() {
  const { t } = useTranslation();
  const { user, loading: authLoading, error: authError } = useAuth();
  const { isSuperAdmin } = useUserRole();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [createMeetingOpen, setCreateMeetingOpen] = useState(false);
  const [joinMeetingOpen, setJoinMeetingOpen] = useState(false);
  const [meetingTitle, setMeetingTitle] = useState("");
  const [meetingDescription, setMeetingDescription] = useState("");
  const [meetingPassword, setMeetingPassword] = useState("");
  const [scheduledStart, setScheduledStart] = useState("");
  const [scheduledEnd, setScheduledEnd] = useState("");
  const [scheduledDuration, setScheduledDuration] = useState("60");
  const [meetingCode, setMeetingCode] = useState("");
  const [joinMeetingPassword, setJoinMeetingPassword] = useState("");
  const [waitingRoomEnabled, setWaitingRoomEnabled] = useState(false);
  const [requirePasscode, setRequirePasscode] = useState(false);
  const [signOutDialogOpen, setSignOutDialogOpen] = useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [templateAction, setTemplateAction] = useState<"new" | "import" | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [recentTemplateIds, setRecentTemplateIds] = useState<string[]>([]);
  const [defaultTemplateId, setDefaultTemplateIdState] = useState<string | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importContent, setImportContent] = useState("");
  const [importMetadata, setImportMetadata] = useState<Record<string, string | number | boolean>>({});
  const [activeCreateTab, setActiveCreateTab] = useState<"instant" | "schedule">("instant");
  const [generatedMeetingCode, setGeneratedMeetingCode] = useState("");
  const [generatedJoinLink, setGeneratedJoinLink] = useState("");
  const [subscription, setSubscription] = useState<{
    subscribed: boolean;
    plan_tier: string | null;
    subscription_end: string | null;
  } | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [creationResult, setCreationResult] = useState<{
    title: string;
    code: string;
    link: string;
    passcode?: string | null;
    requirePasscode: boolean;
    scheduledStart?: string | null;
    scheduledEnd?: string | null;
  } | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { addActivity } = useActivityFeed();
  const [searchParams, setSearchParams] = useSearchParams();
  const shortcutHandledRef = useRef(false);

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate("/login");
      } else {
        fetchProfile();
        fetchMeetings();
        fetchSubscription();
        setLoading(false);
      }
    }
  }, [user, authLoading, navigate]);

  // Auto-refresh subscription status every 60 seconds
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => {
      fetchSubscription();
    }, 60000);
    return () => clearInterval(interval);
  }, [user]);

  // Handle subscription success redirect from Stripe checkout
  useEffect(() => {
    const subscriptionStatus = searchParams.get('subscription');
    if (subscriptionStatus === 'success') {
      toast({
        title: t("dashboard.subscriptionActivatedTitle"),
        description: t("dashboard.subscriptionActivatedDesc"),
      });
      // Refresh subscription status
      fetchSubscription();
      // Clear the query param
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, toast]);

  const fetchSubscription = async () => {
    try {
      setSubscriptionLoading(true);
      const authHeaders = await getSupabaseAuthHeaders();
      if (!authHeaders) {
        setSubscription(null);
        return;
      }

      const { data, error } = await supabase.functions.invoke('check-subscription', {
        headers: authHeaders,
      });
      if (error) throw error;
      setSubscription(data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const getSubscriptionTier = () => {
    if (!subscription?.subscribed || !subscription.plan_tier) return 'Free';
    return subscription.plan_tier.charAt(0).toUpperCase() + subscription.plan_tier.slice(1);
  };


  useEffect(() => {
    const storedRecents = getRecentTemplateIds();
    const storedDefault = getDefaultTemplateId();

    setRecentTemplateIds(storedRecents);
    setDefaultTemplateIdState(storedDefault);

    if (storedDefault) {
      setSelectedTemplateId(storedDefault);
      const { content, metadata } = initializeEditorFromTemplate(storedDefault);
      setImportContent(content);
      setImportMetadata(metadata);
    }
  }, []);

  const getDefaultInstantTitle = () => `Instant meeting on ${format(new Date(), "PP")}`;
  const getDefaultScheduledTitle = () => `Meeting on ${format(new Date(), "PP")}`;

  const formatDateTimeLocal = (date: Date) => {
    const offsetMs = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
  };

  const getDefaultScheduleTimes = (durationMinutes = 60) => {
    const start = new Date();
    start.setMinutes(start.getMinutes() + 30);
    start.setSeconds(0, 0);
    const end = new Date(start.getTime() + durationMinutes * 60 * 1000);

    return {
      start: formatDateTimeLocal(start),
      end: formatDateTimeLocal(end),
    };
  };

  const applyScheduleDefaults = (durationMinutes = Number(scheduledDuration)) => {
    const { start, end } = getDefaultScheduleTimes(durationMinutes);
    setMeetingTitle((current) =>
      current && !current.startsWith("Instant meeting on") ? current : getDefaultScheduledTitle()
    );
    setScheduledStart(start);
    setScheduledEnd(end);
    setScheduledDuration(String(durationMinutes));
    setWaitingRoomEnabled(true);
  };

  const refreshGeneratedDetails = () => {
    const code = generateMeetingCode();
    setGeneratedMeetingCode(code);
    setGeneratedJoinLink(`${window.location.origin}/join/${code}`);
  };

  useEffect(() => {
    if (createMeetingOpen) {
      refreshGeneratedDetails();
      if (activeCreateTab === "instant") {
        setMeetingTitle(getDefaultInstantTitle());
      }
    } else {
      setCreationResult(null);
      setMeetingTitle("");
      setMeetingDescription("");
      setMeetingPassword("");
      setScheduledStart("");
      setScheduledEnd("");
      setScheduledDuration("60");
      setRequirePasscode(false);
      setWaitingRoomEnabled(false);
      setActiveCreateTab("instant");
    }
  }, [createMeetingOpen, activeCreateTab]);

  const handleDefaultChange = (templateId: string | null) => {
    if (templateId === null) {
      clearDefaultTemplate();
    } else {
      setDefaultTemplateId(templateId);
    }

    setDefaultTemplateIdState(templateId);
    setSelectedTemplateId(templateId);
  };

  const handleTemplateSelect = (
    templateId: string,
    options?: { setAsDefault?: boolean; actionOverride?: "new" | "import" | null },
  ) => {
    setSelectedTemplateId(templateId);
    const updatedRecents = addRecentTemplate(templateId);
    setRecentTemplateIds(updatedRecents);

    if (options?.setAsDefault) {
      handleDefaultChange(templateId);
    }

    const actionToUse = options?.actionOverride ?? templateAction;
    if (actionToUse) {
      setTemplateAction(actionToUse);
    }

    const template = getTemplateById(templateId);
    const actionLabel = actionToUse === "import" ? "Import" : "New";

    const { content, metadata } = initializeEditorFromTemplate(templateId);
    setImportContent(content);
    setImportMetadata(metadata);

    toast({
      title: `${actionLabel} template selected`,
      description: template
        ? `${template.name} is ready to use for your next document.`
        : `Template ${templateId} selected.`,
    });

    handleTemplatePickerToggle(false);

    if (actionToUse === "import") {
      setImportDialogOpen(true);
    }
  };

  const handleClearRecents = () => {
    clearRecentTemplates();
    setRecentTemplateIds([]);
  };

  const openTemplatePicker = (action: "new" | "import") => {
    setTemplateAction(action);
    setTemplatePickerOpen(true);
  };

  const handleCreateTabChange = (value: string) => {
    const tabValue = value === "schedule" ? "schedule" : "instant";
    setActiveCreateTab(tabValue);
    if (tabValue === "instant") {
      setMeetingTitle(getDefaultInstantTitle());
      refreshGeneratedDetails();
    } else {
      applyScheduleDefaults();
    }
  };

  const handleScheduleStartChange = (value: string) => {
    setScheduledStart(value);

    if (!value) {
      setScheduledEnd("");
      return;
    }

    const start = new Date(value);
    if (Number.isNaN(start.getTime())) return;

    const end = new Date(start.getTime() + Number(scheduledDuration) * 60 * 1000);
    setScheduledEnd(formatDateTimeLocal(end));
  };

  const handleDurationChange = (value: string) => {
    setScheduledDuration(value);

    if (!scheduledStart) return;

    const start = new Date(scheduledStart);
    if (Number.isNaN(start.getTime())) return;

    const end = new Date(start.getTime() + Number(value) * 60 * 1000);
    setScheduledEnd(formatDateTimeLocal(end));
  };

  const handleTemplatePickerToggle = (open: boolean) => {
    setTemplatePickerOpen(open);
    if (!open) {
      setTemplateAction(null);
    }
  };

  useEffect(() => {
    if (shortcutHandledRef.current) return;
    if (searchParams.get("newNote") === "1") {
      shortcutHandledRef.current = true;
      setTemplateAction("new");
      setTemplatePickerOpen(true);

      const next = new URLSearchParams(searchParams);
      next.delete("newNote");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleImportDocument = () => {
    if (!importContent.trim()) {
      toast({
        title: t("dashboard.importContentRequiredTitle"),
        description: t("dashboard.importContentRequiredDesc"),
        variant: "destructive",
      });
      return;
    }

    setImportDialogOpen(false);

    toast({
      title: t("dashboard.importPreparedTitle"),
      description: t("dashboard.importPreparedDesc"),
    });
  };

  const fetchProfile = async () => {
    if (!user) return;

    const { data: profileData } = await supabase
      .from("profiles")
      .select("display_name, avatar_url")
      .eq("user_id", user.id)
      .single();

    if (profileData) {
      setProfile(profileData);
    }
  };

  const fetchMeetings = async () => {
    if (!user) return;

    const { data, error } = await supabase
      .from("meetings")
      .select("id, title, description, meeting_code, is_active, scheduled_start, scheduled_end, created_at")
      .eq("host_id", user.id)
      .order("created_at", { ascending: false })
      .limit(20);

    if (error) {
      console.error("Error fetching meetings:", error);
      return;
    }

    const list = data || [];
    const ids = list.map((m) => m.id);
    let summaryIds = new Set<string>();
    if (ids.length > 0) {
      const { data: summaries } = await supabase
        .from("meeting_summaries")
        .select("meeting_id")
        .in("meeting_id", ids);
      summaryIds = new Set((summaries || []).map((s: any) => s.meeting_id));
    }
    setMeetings(list.map((m) => ({ ...m, has_summary: summaryIds.has(m.id) })));
  };

  const activeMeetingsCount = meetings.filter((meeting) => meeting.is_active).length;
  const visibleMeetings = meetings.slice(0, 3);
  const nextScheduledMeeting = [...meetings]
    .filter((meeting) => meeting.scheduled_start && new Date(meeting.scheduled_start) >= new Date())
    .sort(
      (a, b) =>
        new Date(a.scheduled_start ?? a.created_at).getTime() -
        new Date(b.scheduled_start ?? b.created_at).getTime()
    )[0];

  const generateMeetingCode = () => {
    return generateCode();
  };

  const createMeeting = async () => {
    if (!user || !meetingTitle.trim()) return;

    try {
      const code = generatedMeetingCode || generateMeetingCode();
      const joinLink = generatedJoinLink || `${window.location.origin}/join/${code}`;
      const passwordToUse = requirePasscode ? meetingPassword || generateMeetingCode().slice(0, 6) : "";
      let scheduleEndToUse = scheduledEnd;
      if (activeCreateTab === "schedule" && scheduledStart && !scheduledEnd) {
        const startTime = new Date(scheduledStart).getTime();
        if (!Number.isNaN(startTime)) {
          scheduleEndToUse = formatDateTimeLocal(new Date(startTime + Number(scheduledDuration) * 60 * 1000));
        }
      }

      const validation = meetingSchema.safeParse({
        title: meetingTitle,
        description: meetingDescription,
        code: code,
        password: passwordToUse ? passwordToUse : undefined,
      });

      if (!validation.success) {
        toast({
          title: t("dashboard.validationErrorTitle"),
          description: validation.error.errors[0].message,
          variant: "destructive",
        });
        return;
      }

      if (activeCreateTab === "schedule") {
        if (!scheduledStart || !scheduleEndToUse) {
          toast({
            title: t("dashboard.scheduleRequiredTitle"),
            description: t("dashboard.scheduleRequiredDesc"),
            variant: "destructive",
          });
          return;
        }

        const startDate = new Date(scheduledStart);
        const endDate = new Date(scheduleEndToUse);

        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime()) || endDate <= startDate) {
          toast({
            title: t("dashboard.invalidScheduleTitle"),
            description: t("dashboard.invalidScheduleDesc"),
            variant: "destructive",
          });
          return;
        }
      }

      const hashedPassword = passwordToUse ? await hashPassword(passwordToUse) : null;

      const { data, error } = await supabase
        .from("meetings")
        .insert({
          title: validation.data.title,
          description: validation.data.description || null,
          host_id: user.id,
          meeting_code: code,
          is_active: activeCreateTab === "instant",
          scheduled_start: activeCreateTab === "schedule" ? scheduledStart : null,
          scheduled_end: activeCreateTab === "schedule" ? scheduleEndToUse : null,
          waiting_room_enabled: waitingRoomEnabled,
          password: hashedPassword,
        })
        .select()
        .single();

      if (error) throw error;

      toast({
        title: t("dashboard.meetingCreatedTitle"),
        description: t("dashboard.meetingCreatedDesc", { code }),
      });

        addActivity({
          action: "created",
          actor: { id: user.id, name: profile?.display_name || user.email },
          target: { title: t("dashboard.activity.meetingCreated", { title: validation.data.title }) },
        });

      setCreationResult({
        title: validation.data.title,
        code,
        link: joinLink,
        passcode: passwordToUse || null,
        requirePasscode,
        scheduledStart: activeCreateTab === "schedule" ? scheduledStart : null,
        scheduledEnd: activeCreateTab === "schedule" ? scheduleEndToUse : null,
      });

      setMeetingTitle(activeCreateTab === "instant" ? getDefaultInstantTitle() : "");
      setMeetingDescription("");
      setMeetingPassword(passwordToUse || "");
      setScheduledStart("");
      setScheduledEnd("");
      setScheduledDuration("60");
      setWaitingRoomEnabled(false);
      setRequirePasscode(false);
      refreshGeneratedDetails();
      fetchMeetings();

      if (activeCreateTab === "instant") {
        setCreateMeetingOpen(false);
        navigate(`/meeting/${data.id}`);
      }
    } catch (error: any) {
      const isRateLimit = handleRateLimitError(error, "meeting_create");

      if (!isRateLimit) {
        const { title, description } = getErrorMessage(error);
        toast({
          title,
          description,
          variant: "destructive",
        });
      }
    }
  };

  const getInviteDetails = () => {
    return {
      title: creationResult?.title || meetingTitle || "Meeting",
      link: creationResult?.link || generatedJoinLink,
      passcode: creationResult?.passcode || meetingPassword || null,
    };
  };

  const handleCopyValue = async (value: string, title: string, description?: string) => {
    await navigator.clipboard.writeText(value);
    toast({ title, description });
  };

  const handleCopyInvite = () => {
    const { title, link, passcode } = getInviteDetails();
    const invite = t("dashboard.createMeeting.inviteMessage", {
      title,
      link,
      passcodeLine: passcode ? t("dashboard.createMeeting.invitePasscodeLine", { passcode }) : "",
    });
    handleCopyValue(
      invite,
      t("dashboard.createMeeting.inviteCopiedTitle"),
      t("dashboard.createMeeting.inviteCopiedDescription")
    );
  };

  const getCalendarTimes = () => {
    const start = creationResult?.scheduledStart || scheduledStart || new Date().toISOString();
    const end = creationResult?.scheduledEnd || scheduledEnd;
    const startDate = new Date(start);
    const endDate = end ? new Date(end) : new Date(startDate.getTime() + 30 * 60 * 1000);

    return { startDate, endDate };
  };

  const openCalendarLink = (provider: "google" | "outlook") => {
    const { title, link, passcode } = getInviteDetails();
    const { startDate, endDate } = getCalendarTimes();
    const start = format(startDate, "yyyyMMdd'T'HHmmss'Z'");
    const end = format(endDate, "yyyyMMdd'T'HHmmss'Z'");
    const details = t("dashboard.createMeeting.calendarDetails", {
      link,
      passcodeLine: passcode ? t("dashboard.createMeeting.invitePasscodeLine", { passcode }) : "",
    });

    if (provider === "google") {
      const googleUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${start}/${end}&details=${encodeURIComponent(details)}&location=${encodeURIComponent(link)}`;
      window.open(googleUrl, "_blank");
      return;
    }

    const outlookUrl = `https://outlook.live.com/calendar/0/deeplink/compose?path=/calendar/action/compose&rru=addevent&subject=${encodeURIComponent(title)}&body=${encodeURIComponent(details)}&startdt=${encodeURIComponent(startDate.toISOString())}&enddt=${encodeURIComponent(endDate.toISOString())}`;
    window.open(outlookUrl, "_blank");
  };

  const downloadIcs = () => {
    const { title, link, passcode } = getInviteDetails();
    const { startDate, endDate } = getCalendarTimes();
    const start = format(startDate, "yyyyMMdd'T'HHmmss'Z'");
    const end = format(endDate, "yyyyMMdd'T'HHmmss'Z'");
    const description = t("dashboard.createMeeting.calendarDetails", {
      link,
      passcodeLine: passcode ? `\\n${t("dashboard.createMeeting.invitePasscodeLine", { passcode })}` : "",
    });

    const icsContent = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//MeetHub//EN",
      "CALSCALE:GREGORIAN",
      "BEGIN:VEVENT",
      `DTSTART:${start}`,
      `DTEND:${end}`,
      `SUMMARY:${title}`,
      `DESCRIPTION:${description}`,
      `LOCATION:${link}`,
      "END:VEVENT",
      "END:VCALENDAR",
    ].join("\n");

    const blob = new Blob([icsContent], { type: "text/calendar" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${title.replace(/\s+/g, "-").toLowerCase()}.ics`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const renderCreateForm = (isScheduled: boolean) => (
    <div className="space-y-5">
      <div className="rounded-lg border border-border/70 bg-muted/20 p-4">
        <div className="flex items-start gap-3">
          <div className="rounded-md bg-primary/10 p-2">
            {isScheduled ? <Calendar className="h-5 w-5 text-primary" /> : <Video className="h-5 w-5 text-primary" />}
          </div>
          <div>
            <p className="font-medium">{isScheduled ? "Schedule a meeting" : "Start an instant meeting"}</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {isScheduled
                ? "Pick a topic, start time, and duration. The join link is created automatically."
                : "Create a room and enter it right away. You can copy the link before starting."}
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-4">
        <div className="space-y-2">
          <Label htmlFor={isScheduled ? "schedule-title" : "instant-title"}>
            {isScheduled ? "Meeting topic" : "Meeting name"}
          </Label>
          <Input
            id={isScheduled ? "schedule-title" : "instant-title"}
            placeholder={isScheduled ? "Team sync" : "Quick meeting"}
            value={meetingTitle}
            onChange={(e) => setMeetingTitle(e.target.value)}
            maxLength={200}
          />
        </div>

        {isScheduled && (
          <div className="grid gap-4 sm:grid-cols-[1fr_160px]">
            <div className="space-y-2">
              <Label htmlFor="start">Date and time</Label>
              <Input
                id="start"
                type="datetime-local"
                value={scheduledStart}
                onChange={(e) => handleScheduleStartChange(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="duration">Duration</Label>
              <select
                id="duration"
                value={scheduledDuration}
                onChange={(e) => handleDurationChange(e.target.value)}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <option value="30">30 minutes</option>
                <option value="60">1 hour</option>
                <option value="90">1.5 hours</option>
                <option value="120">2 hours</option>
              </select>
            </div>
          </div>
        )}
      </div>

      <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-medium">Meeting link</p>
            <p className="mt-1 font-mono text-sm text-muted-foreground break-all">{generatedJoinLink}</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              handleCopyValue(
                generatedJoinLink,
                t("dashboard.createMeeting.copyLinkTitle"),
                t("dashboard.createMeeting.copyLinkDescription")
              )
            }
          >
            <Copy className="mr-2 h-4 w-4" />
            Copy
          </Button>
        </div>
        <div className="mt-3 flex items-center justify-between rounded-md bg-background px-3 py-2">
          <span className="text-sm text-muted-foreground">Code</span>
          <span className="font-mono font-semibold">{generatedMeetingCode}</span>
        </div>
      </div>

      <div className="rounded-lg border border-border p-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <Label>{isScheduled ? "Require passcode" : "Add passcode"}</Label>
            <p className="text-sm text-muted-foreground">
              {isScheduled ? "Useful when sharing scheduled links widely." : "Optional for quick rooms."}
            </p>
          </div>
          <Switch checked={requirePasscode} onCheckedChange={setRequirePasscode} />
        </div>

        {requirePasscode && (
          <div className="mt-4 space-y-2">
            <Label htmlFor="password">Passcode</Label>
            <PasswordInput
              id="password"
              value={meetingPassword}
              onChange={(e) => setMeetingPassword(e.target.value)}
              placeholder="Auto-generate if left blank"
              autoComplete="new-password"
            />
          </div>
        )}
      </div>

      {isScheduled && scheduledStart && scheduledEnd && (
        <div className="rounded-lg bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
          Ends at {format(new Date(scheduledEnd), "PPp")}
        </div>
      )}

      <div className="flex justify-end">
        <Button
          onClick={createMeeting}
          className="min-w-[200px]"
          disabled={!meetingTitle.trim() || (isScheduled && !scheduledStart)}
        >
          {isScheduled ? "Schedule meeting" : "Create and start"}
        </Button>
      </div>
    </div>
  );

  const joinMeeting = async () => {
    if (!meetingCode.trim()) return;

    try {
      // Use the secure function to verify meeting password
      const { data, error } = await supabase
        .rpc("verify_meeting_password", {
          _meeting_code: meetingCode.toUpperCase(),
          _password: joinMeetingPassword || null,
        })
        .single();

      if (error || !data || !data.is_valid) {
        toast({
          title: t("dashboard.meetingNotFoundTitle"),
          description: joinMeetingPassword
            ? t("dashboard.meetingNotFoundWithPassword")
            : t("dashboard.meetingNotFound"),
          variant: "destructive",
        });
        return;
      }

      setJoinMeetingOpen(false);
      setJoinMeetingPassword("");
      setMeetingCode("");
      navigate(`/meeting/${data.meeting_id}`);
    } catch (error: any) {
      const { title, description } = getErrorMessage(error);
      toast({
        title,
        description,
        variant: "destructive",
      });
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/login");
  };

  if (authError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="text-destructive">{t("dashboard.connectionErrorTitle")}</CardTitle>
            <CardDescription>{t("dashboard.connectionErrorDesc")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">{authError}</p>
            <p className="text-sm text-muted-foreground">
              {t("dashboard.connectionErrorHint")}
            </p>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
              <li>{t("dashboard.connectionErrorTips.connection")}</li>
              <li>{t("dashboard.connectionErrorTips.staging")}</li>
              <li>{t("dashboard.connectionErrorTips.dns")}</li>
            </ul>
            <Button onClick={() => window.location.reload()} className="w-full">
              {t("dashboard.retryConnection")}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Video className="h-12 w-12 text-primary mx-auto mb-4 animate-pulse" />
          <p className="text-muted-foreground">{t("common.loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,hsl(var(--primary)/0.08),transparent_30%),linear-gradient(180deg,hsl(var(--background)),hsl(var(--muted)/0.18))]">
      {/* Header */}
      <header className="border-b border-border/60 bg-background/85 backdrop-blur-xl">
        <div className="container mx-auto flex items-center justify-between px-4 py-4">
          <div className="flex items-center space-x-3">
            <Video className="h-8 w-8 text-primary" />
            <div className="flex items-center">
              <h1 className="text-2xl font-bold">MeetHub</h1>
              
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <span className="hidden md:inline text-sm text-muted-foreground">
              {t("dashboard.welcome", { name: profile?.display_name || user?.email })}
            </span>
            <Button
              variant="secondary"
              size="sm"
              className="hidden md:inline-flex items-center gap-2"
              onClick={() => setProfileDialogOpen(true)}
            >
              <Pencil className="h-4 w-4" />
              {t("nav.editProfile")}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-tour="account-menu" variant="outline" size="sm" className="flex items-center gap-2">
                  <Settings className="h-4 w-4" />
                  {t("dashboard.account")}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="line-clamp-2">{profile?.display_name || user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {isSuperAdmin && (
                  <DropdownMenuItem
                    onSelect={(event) => {
                      event.preventDefault();
                      navigate("/admin");
                    }}
                  >
                    <Shield className="h-4 w-4 mr-2" />
                    {t("dashboard.adminPanel")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onSelect={(event) => {
                    event.preventDefault();
                    setProfileDialogOpen(true);
                  }}
                >
                  <Pencil className="h-4 w-4 mr-2" />
                  {t("nav.editProfile")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={(event) => {
                    event.preventDefault();
                    navigate("/profile");
                  }}
                >
                  <Settings className="h-4 w-4 mr-2" />
                  {t("dashboard.profileSettings")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={(event) => {
                    event.preventDefault();
                    setSignOutDialogOpen(true);
                  }}
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  {t("nav.logOut")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <AlertDialog open={signOutDialogOpen} onOpenChange={setSignOutDialogOpen}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t("dashboard.signOutTitle")}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t("dashboard.signOutDesc")}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                  <AlertDialogAction onClick={handleSignOut}>{t("dashboard.signOutAction")}</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Email Verification Banner */}
        {user && !user.email_confirmed_at && user.email && (
          <EmailVerificationBanner email={user.email} />
        )}

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
          <section className="space-y-6">
            <Card className="border-border/60 bg-background/90 shadow-sm">
              <CardContent className="p-6">
                <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Welcome back</p>
                    <h2 className="mt-1 text-2xl font-semibold tracking-tight">
                      {profile?.display_name || user?.email}
                    </h2>
                    <p className="mt-2 max-w-xl text-sm text-muted-foreground">
                      Start a meeting, join with a code, or reopen a recent room.
                    </p>
                  </div>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <Button size="lg" onClick={() => setCreateMeetingOpen(true)} data-tour="create-session">
                      <Plus className="mr-2 h-5 w-5" />
                      Create Meeting
                    </Button>
                    <Button size="lg" variant="outline" onClick={() => setJoinMeetingOpen(true)}>
                      <Users className="mr-2 h-5 w-5" />
                      Join Meeting
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card data-tour="meetings-list" className="border-border/60 bg-background/90 shadow-sm">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <CardTitle className="flex items-center text-lg">
                      <Calendar className="mr-2 h-5 w-5" />
                      Recent meetings
                    </CardTitle>
                    <CardDescription>Showing only the latest 3 rooms.</CardDescription>
                  </div>
                  {meetings.length > visibleMeetings.length && (
                    <Badge variant="secondary">{meetings.length} total</Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {visibleMeetings.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-border bg-muted/20 p-6 text-center">
                    <p className="font-medium">No meetings yet</p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Create your first room when you are ready.
                    </p>
                    <Button className="mt-4" onClick={() => setCreateMeetingOpen(true)}>
                      <Plus className="mr-2 h-4 w-4" />
                      Create Meeting
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {visibleMeetings.map((meeting) => (
                      <div
                        key={meeting.id}
                        className="flex flex-col gap-3 rounded-lg border border-border/70 bg-background p-4 md:flex-row md:items-center md:justify-between"
                      >
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="truncate font-medium">{meeting.title}</h3>
                            <Badge variant={meeting.is_active ? "default" : "secondary"} className="text-xs">
                              {meeting.is_active ? "Live" : "Ended"}
                            </Badge>
                          </div>
                          <p className="mt-1 text-sm text-muted-foreground">
                            Code {meeting.meeting_code}
                          </p>
                          {meeting.scheduled_start && (
                            <p className="mt-1 text-xs text-muted-foreground">
                              {format(new Date(meeting.scheduled_start), "PPp")}
                            </p>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const link = `${window.location.origin}/join/${meeting.meeting_code}`;
                              navigator.clipboard.writeText(link);
                              toast({
                                title: t("dashboard.recentMeetings.linkCopiedTitle"),
                                description: t("dashboard.recentMeetings.linkCopiedDesc"),
                              });
                            }}
                          >
                            <Copy className="mr-2 h-4 w-4" />
                            Copy link
                          </Button>
                          <Button size="sm" onClick={() => navigate(`/meeting/${meeting.id}`)} disabled={!meeting.is_active}>
                            {meeting.is_active ? "Open" : "Closed"}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </section>

          <aside className="space-y-4">
            <Card className="border-border/60 bg-background/90 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Status</CardTitle>
                <CardDescription>Only the essentials.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground">Plan</p>
                  <div className="mt-1 flex items-center gap-2">
                    <Crown className="h-4 w-4 text-primary" />
                    <span className="font-medium">
                      {subscriptionLoading ? t("common.loading") : getSubscriptionTier()}
                    </span>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Active rooms</p>
                  <p className="mt-1 text-2xl font-semibold">{activeMeetingsCount}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Next meeting</p>
                  <p className="mt-1 line-clamp-1 font-medium">
                    {nextScheduledMeeting?.title || "None scheduled"}
                  </p>
                  {nextScheduledMeeting?.scheduled_start && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      {format(new Date(nextScheduledMeeting.scheduled_start), "PPp")}
                    </p>
                  )}
                </div>
                <Button variant="outline" size="sm" className="w-full" onClick={() => navigate("/pricing")}>
                  {subscription?.subscribed ? t("dashboard.managePlan") : t("dashboard.upgrade")}
                </Button>
              </CardContent>
            </Card>

            <Card className="border-border/60 bg-background/90 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Account</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button variant="outline" className="w-full justify-start" onClick={() => setProfileDialogOpen(true)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  {t("nav.editProfile")}
                </Button>
                <Button variant="outline" className="w-full justify-start" onClick={() => navigate("/profile")}>
                  <Settings className="mr-2 h-4 w-4" />
                  {t("dashboard.profileSettings")}
                </Button>
              </CardContent>
            </Card>
          </aside>
        </div>

        <Dialog open={createMeetingOpen} onOpenChange={setCreateMeetingOpen}>
                <DialogContent className="w-[95vw] max-w-3xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
                  <DialogHeader className="space-y-2 sm:space-y-3">
                    <DialogTitle className="text-lg sm:text-xl">{t("dashboard.createMeeting.dialogTitle")}</DialogTitle>
                    <DialogDescription className="text-sm sm:text-base">
                      {t("dashboard.createMeeting.dialogDescription")}
                    </DialogDescription>
                  </DialogHeader>
                  <Tabs value={activeCreateTab} onValueChange={handleCreateTabChange} className="space-y-4">
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="instant">{t("dashboard.createMeeting.tabs.new")}</TabsTrigger>
                      <TabsTrigger value="schedule">{t("dashboard.createMeeting.tabs.schedule")}</TabsTrigger>
                    </TabsList>
                    <TabsContent value="instant" className="space-y-4">
                      {renderCreateForm(false)}
                    </TabsContent>
                    <TabsContent value="schedule" className="space-y-4">
                      {renderCreateForm(true)}
                    </TabsContent>
                  </Tabs>

                  {creationResult && (
                    <Card className="mt-4 border-primary/30 bg-primary/5">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Sparkles className="h-5 w-5 text-primary" />
                          {t("dashboard.createMeeting.readyTitle")}
                        </CardTitle>
                        <CardDescription>{t("dashboard.createMeeting.readyDescription")}</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid gap-3 md:grid-cols-2">
                          <div className="space-y-1">
                            <p className="text-sm text-muted-foreground">{t("dashboard.createMeeting.fields.title")}</p>
                            <p className="font-semibold">{creationResult.title}</p>
                            {creationResult.scheduledStart && (
                              <p className="text-xs text-muted-foreground">
                                {format(new Date(creationResult.scheduledStart), "PPp")}
                              </p>
                            )}
                          </div>
                          <div className="space-y-1">
                            <p className="text-sm text-muted-foreground">{t("dashboard.createMeeting.fields.code")}</p>
                            <div className="flex items-center gap-2">
                              <Badge variant="secondary" className="font-mono text-sm py-1 px-2">
                                {creationResult.code}
                              </Badge>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() =>
                                  handleCopyValue(
                                    creationResult.code,
                                    t("dashboard.createMeeting.copyCodeTitle"),
                                    t("dashboard.createMeeting.copyCodeDescription")
                                  )
                                }
                              >
                                {t("dashboard.createMeeting.copyAction")}
                              </Button>
                            </div>
                          </div>
                          <div className="space-y-1">
                            <p className="text-sm text-muted-foreground">{t("dashboard.createMeeting.fields.joinLink")}</p>
                            <div className="flex flex-col gap-2">
                              <span className="font-mono text-sm break-all">{creationResult.link}</span>
                              <div className="flex flex-wrap gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() =>
                                    handleCopyValue(
                                      creationResult.link,
                                      t("dashboard.createMeeting.copyLinkTitle"),
                                      t("dashboard.createMeeting.copyLinkDescription")
                                    )
                                  }
                                >
                                  {t("dashboard.createMeeting.copyLinkAction")}
                                </Button>
                                <Button size="sm" variant="secondary" onClick={handleCopyInvite}>
                                  {t("dashboard.createMeeting.copyInviteAction")}
                                </Button>
                              </div>
                            </div>
                          </div>
                          <div className="space-y-1">
                            <p className="text-sm text-muted-foreground">{t("dashboard.createMeeting.fields.passcode")}</p>
                            <div className="flex items-center gap-2">
                              <Badge variant="secondary">
                                {creationResult.passcode || t("dashboard.createMeeting.passcodeNone")}
                              </Badge>
                              {creationResult.passcode && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() =>
                                    handleCopyValue(
                                      creationResult.passcode!,
                                      t("dashboard.createMeeting.copyPasscodeTitle"),
                                      t("dashboard.createMeeting.copyPasscodeDescription")
                                    )
                                  }
                                >
                                  {t("dashboard.createMeeting.copyAction")}
                                </Button>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {t("dashboard.createMeeting.passcodeStatus", {
                                status: creationResult.requirePasscode
                                  ? t("dashboard.createMeeting.status.required")
                                  : t("dashboard.createMeeting.status.optional"),
                              })}
                            </p>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <p className="text-sm font-semibold">{t("dashboard.createMeeting.shareLabel")}</p>
                          <div className="flex flex-wrap gap-2">
                            <Button variant="outline" size="sm" onClick={() => openCalendarLink("google")}>
                              {t("dashboard.calendar.google")}
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => openCalendarLink("outlook")}>
                              {t("dashboard.calendar.outlook")}
                            </Button>
                            <Button variant="outline" size="sm" onClick={downloadIcs}>
                              {t("dashboard.calendar.ics")}
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </DialogContent>
        </Dialog>

        <Dialog open={joinMeetingOpen} onOpenChange={setJoinMeetingOpen}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{t("dashboard.joinMeeting.dialogTitle")}</DialogTitle>
                    <DialogDescription>{t("dashboard.joinMeeting.dialogDescription")}</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="code">{t("meeting.meetingCode")}</Label>
                      <Input
                        id="code"
                        placeholder={t("meeting.meetingCodePlaceholder")}
                        value={meetingCode}
                        onChange={(e) => setMeetingCode(e.target.value.toUpperCase())}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="join-password">{t("dashboard.joinMeeting.passwordLabel")}</Label>
                      <PasswordInput
                        id="join-password"
                        placeholder={t("dashboard.joinMeeting.passwordPlaceholder")}
                        value={joinMeetingPassword}
                        onChange={(e) => setJoinMeetingPassword(e.target.value)}
                      />
                    </div>
                    <Button onClick={joinMeeting} className="w-full" disabled={!meetingCode.trim()}>
                      {t("meeting.joinButton")}
                    </Button>
                  </div>
                </DialogContent>
        </Dialog>

        <TemplatePickerModal
          open={templatePickerOpen}
          onOpenChange={handleTemplatePickerToggle}
          onSelect={handleTemplateSelect}
          recentTemplateIds={recentTemplateIds}
          defaultTemplateId={defaultTemplateId}
          onSetDefault={handleDefaultChange}
          onClearRecents={handleClearRecents}
          action={templateAction}
        />

        <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Import from template</DialogTitle>
              <DialogDescription>
                Start from the template defaults, then paste or edit your text before importing.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="import-content">Document content</Label>
                <Textarea
                  id="import-content"
                  value={importContent}
                  onChange={(event) => setImportContent(event.target.value)}
                  className="min-h-[160px]"
                />
                <p className="text-xs text-muted-foreground">
                  Template defaults are pre-populated. Paste your transcript or notes to merge with the selected
                  metadata.
                </p>
              </div>
              <div className="space-y-2">
                <Label>Metadata to attach</Label>
                <div className="flex flex-wrap gap-2 rounded-md border border-dashed border-border bg-muted/40 p-3">
                  {Object.keys(importMetadata).length === 0 ? (
                    <span className="text-sm text-muted-foreground">No metadata provided for this template.</span>
                  ) : (
                    Object.entries(importMetadata).map(([key, value]) => (
                      <span
                        key={key}
                        className="rounded-full bg-background px-3 py-1 text-xs font-medium text-foreground border border-border"
                      >
                        {key}: {String(value)}
                      </span>
                    ))
                  )}
                </div>
              </div>
              <Button onClick={handleImportDocument} className="w-full">
                Prepare import
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <EditProfileDialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen} />

      <Footer />
    </div>
  );
}

